import { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function NotFound() {
  const location = useLocation();
  const { user } = useAuth();
  const reportSubject = encodeURIComponent('Grupo Bienestar - Enlace roto');
  const reportBody = encodeURIComponent(
    `Hola equipo,\n\nDetecté una ruta no encontrada.\n\nRuta: ${location.pathname || '/'}\nFecha: ${new Date().toISOString()}\n\nDetalle adicional:\n`
  );

  const routeCatalog = useMemo(
    () => [
      { to: '/', label: 'Inicio' },
      { to: '/conversaciones', label: 'Conversaciones', permission: 'ver_conversaciones' },
      { to: '/asociacion-clientes', label: 'Contactos', permission: 'ver_contactos' },
      {
        to: '/gestion-casos',
        label: 'Gestion de Casos',
        permission: 'ver_trazabilidad_asignaciones'
      },
      { to: '/pedidos', label: 'Pedidos', permission: 'ver_pedidos' },
      { to: '/contact-center', label: 'Contact Center', permission: 'ver_pedidos_dispensa' },
      { to: '/bitacora-operativa', label: 'Trazabilidad', permission: 'ver_pulse_ledger' },
      { to: '/delivery', label: 'Última milla', permission: 'ver_pedidos' },
      { to: '/pedidos-recetas-medico', label: 'Pedidos/Recetas', permission: 'ver_pedido_recetas_medico' },
      { to: '/recetas-cruzadas', label: 'Recetas cruzadas', permission: 'ver_recetas_cruzadas' },
      { to: '/recetas-emitir', label: 'Recetas a emitir', permission: 'ver_recetas_emitir' },
    ],
    []
  );

  const suggestions = useMemo(() => {
    const perms = Array.isArray(user?.permissions) ? user.permissions : [];
    const canSee = (route) => !route.permission || perms.includes(route.permission);
    const visible = routeCatalog.filter(canSee);
    const current = String(location.pathname || '/').toLowerCase();
    const score = (target) => {
      const t = String(target || '/').toLowerCase();
      if (t === current) return -1;
      if (current.includes(t) || t.includes(current)) return 100;
      const currentParts = current.split('/').filter(Boolean);
      const targetParts = t.split('/').filter(Boolean);
      let common = 0;
      for (const p of currentParts) if (targetParts.includes(p)) common += 1;
      return common * 10 - Math.abs(targetParts.length - currentParts.length);
    };
    return visible
      .map((r) => ({ ...r, _score: score(r.to) }))
      .sort((a, b) => b._score - a._score)
      .slice(0, 4);
  }, [location.pathname, routeCatalog, user?.permissions]);

  useEffect(() => {
    try {
      const key = 'pulse_not_found_events';
      const prev = JSON.parse(localStorage.getItem(key) || '[]');
      const next = [
        {
          path: location.pathname || '/',
          at: new Date().toISOString(),
          user: user?.username || null
        },
        ...prev
      ].slice(0, 50);
      localStorage.setItem(key, JSON.stringify(next));
    } catch (_) {
      // noop: non-blocking analytics
    }
  }, [location.pathname, user?.username]);

  return (
    <div
      className="nf-root"
      style={{
        padding: '28px 20px',
        minHeight: 'calc(100vh - 56px)',
        background:
          'radial-gradient(1200px 420px at 100% -10%, rgba(37,99,235,.12), transparent 58%), radial-gradient(900px 320px at -10% 110%, rgba(16,185,129,.10), transparent 58%)',
        animation: 'notFoundFadeIn .35s ease-out'
      }}
    >
      <style>{`
        @keyframes notFoundFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .nf-card {
          max-width: 980px;
          margin: 0 auto;
        }
        .nf-grid {
          margin-top: 14px;
          display: grid;
          grid-template-columns: 1.25fr .75fr;
          gap: 14px;
        }
        .nf-actions {
          display: flex;
          gap: 10px;
          margin-top: 16px;
          flex-wrap: wrap;
        }
        @media (max-width: 1024px) {
          .nf-card {
            max-width: 100%;
          }
          .nf-grid {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 640px) {
          .nf-root {
            padding: 16px 12px !important;
          }
          .nf-head {
            gap: 10px !important;
          }
          .nf-title {
            font-size: 26px !important;
          }
          .nf-actions > a,
          .nf-actions > button {
            width: 100%;
            text-align: center;
            justify-content: center;
          }
          .nf-path {
            width: 100%;
            word-break: break-all;
          }
        }
      `}</style>
      <div
        className="nf-card"
        style={{
          background: 'linear-gradient(180deg,#ffffff,#f8fafc 42%, #f1f5f9 100%)',
          border: '1px solid rgba(148,163,184,.24)',
          borderRadius: 20,
          boxShadow: '0 26px 56px rgba(15,23,42,.12)',
          padding: '26px 24px'
        }}
      >
        <div className="nf-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: '#64748b', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 999, padding: '4px 10px' }}>
            <span>404</span>
            <span>Not Found</span>
          </div>
          <div className="nf-path" style={{ fontFamily: 'monospace', fontSize: 12, color: '#64748b', background: 'rgba(255,255,255,.85)', border: '1px solid rgba(148,163,184,.28)', borderRadius: 10, padding: '6px 10px' }}>
            pulse://{location.pathname || '/'}
          </div>
        </div>

        <div className="nf-grid">
          <div style={{ background: '#fff', border: '1px solid rgba(148,163,184,.2)', borderRadius: 14, padding: '16px 16px 14px' }}>
            <h1 className="nf-title" style={{ margin: 0, fontSize: 34, lineHeight: 1.05, color: '#0f172a', letterSpacing: '-0.02em' }}>
              Ruta no encontrada
            </h1>
            <p style={{ margin: '10px 0 0', color: '#475569', fontSize: 15, lineHeight: 1.45 }}>
              La URL que intentaste abrir no existe o fue movida. Podés volver al inicio o regresar a la vista anterior.
            </p>
            <div style={{ marginTop: 12, background: 'linear-gradient(180deg,#f8fafc,#eef2ff)', border: '1px solid #c7d2fe', borderRadius: 12, padding: '10px 12px' }}>
              <svg width="100%" height="64" viewBox="0 0 420 64" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
                <path d="M8 48C35 28 64 28 92 48C120 68 148 68 176 48C204 28 232 28 260 48C288 68 316 68 344 48C372 28 397 28 412 36" stroke="#2563EB" strokeWidth="3" strokeLinecap="round"/>
                <circle cx="176" cy="48" r="6" fill="#10B981"/>
                <circle cx="260" cy="48" r="6" fill="#2563EB"/>
                <circle cx="344" cy="48" r="6" fill="#F59E0B"/>
                <text x="10" y="18" fill="#475569" fontSize="12" fontWeight="700">Ruta rota detectada</text>
              </svg>
            </div>
            <div className="nf-actions">
              <Link to="/" style={{ textDecoration: 'none', background: 'linear-gradient(180deg,#2563eb,#1d4ed8)', color: '#fff', borderRadius: 10, padding: '10px 14px', fontWeight: 700, boxShadow: '0 10px 20px rgba(37,99,235,.28)' }}>
                Ir al inicio
              </Link>
              <button
                type="button"
                onClick={() => window.history.back()}
                style={{ border: '1px solid rgba(148,163,184,.45)', background: '#fff', color: '#0f172a', borderRadius: 10, padding: '10px 14px', fontWeight: 700, cursor: 'pointer' }}
              >
                Volver atrás
              </button>
              <a
                href={`mailto:soporte@pulseone.local?subject=${reportSubject}&body=${reportBody}`}
                style={{ textDecoration: 'none', border: '1px solid rgba(148,163,184,.45)', background: '#fff', color: '#0f172a', borderRadius: 10, padding: '10px 14px', fontWeight: 700 }}
              >
                Reportar enlace roto
              </a>
            </div>
          </div>

          <div style={{ background: 'linear-gradient(180deg,#ffffff,#f8fafc)', border: '1px solid rgba(148,163,184,.2)', borderRadius: 14, padding: '14px 14px' }}>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
              Diagnóstico rápido
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, color: '#334155', fontSize: 13, lineHeight: 1.5 }}>
              <li>Revisá si la URL está bien escrita.</li>
              <li>Confirmá que tu perfil tenga acceso a esa sección.</li>
              <li>Si era un link guardado, puede haber cambiado.</li>
            </ul>
            <div style={{ marginTop: 10, fontSize: 10, color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em' }}>
              Sugerencias
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              {suggestions.map((s) => (
                <Link
                  key={s.to}
                  to={s.to}
                  style={{
                    textDecoration: 'none',
                    background: '#fff',
                    border: '1px solid rgba(148,163,184,.34)',
                    borderRadius: 8,
                    padding: '7px 9px',
                    color: '#1e293b',
                    fontSize: 12,
                    fontWeight: 600
                  }}
                >
                  {s.label}
                </Link>
              ))}
            </div>
            <div style={{ marginTop: 10, background: '#fff', border: '1px dashed rgba(148,163,184,.55)', borderRadius: 10, padding: '8px 10px' }}>
              <div style={{ fontSize: 10, color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em' }}>Ruta solicitada</div>
              <div style={{ marginTop: 4, fontFamily: 'monospace', color: '#0f172a', fontSize: 12, wordBreak: 'break-all' }}>
                {location.pathname || '/'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotFound;
